/********************************/
/*          About vuex          */
/********************************/
// The MIT License (MIT)
//
// Copyright (c) 2015-present Evan You
//
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in
// all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
// THE SOFTWARE.

import { mapState, mapActions } from 'vuex';
import individualDiagnose from '../../../../share/util/individualDiagnose';
import individualWorkSupportUtil from '../../../../share/util/individualWorkSupportUtil';
import config from '../../../../share/config';
import session from '../../../../share/session';


/** 各フェーズ定義 */
const PHASE = {
  START: 'start',
  FAILED: 'failed',
  SUCCESS: 'success'
};

export default {
  name: 'requirement001',
  data() {
    return {
      phase: PHASE.START,
      isLoading: false,
      displayInfo: '',
      watcher: null,
      // ダイアログフラグ
      modal_requirement_001: false,
      sessionTransitionFlag: false // セッション遷移を監視するフラグ
    };
  },

  props: {
    i18nWsKey: { type: String, default: '', required: true },
    workSupportId: { type: Number, default: '', required: true },
  },

  computed: {
    ...mapState({
      netAppId: state => state.netAppConnector.pairingAppId
    }),
    /**
     * ダイアログのヘッダー色
     */
    headerColor() {
      switch (this.phase) {
        case PHASE.FAILED:
          return 'danger';
        case PHASE.SUCCESS:
          return 'success';
        case PHASE.START:
        default:
          return 'secondary';
      }
    }
  },

  methods: {
    ...mapActions('auth', ['logout', 'resetSession']),

    /** 初期化処理 */
    init() {
      this.modal_requirement_001 = true;
    },

    /** Wizardを閉じる */
    async closeWizard() {
      this.$emit('close-wizard');
      this.modal_requirement_001 = false;
    },

    /**
     * API 通信で共通バリデーションでエラーが発生した際のコールバック
     * @param {object} result バリデーション結果
     */
    validateErrorResult(result) {
      individualDiagnose.apiErrorDefaultAction(
        this,
        result,
        // ログアウト処理のコールバック
        (self) => { self; this.logout(this); },
        // セッションリセット処理のコールバック
        (self) => { self; this.resetSession(this); },
        // その他エラー発生のコールバック
        (result) => { result; this.showUnexpectedError(); },
      );
    },

    /** セッション遷移を伴うデータ読み出し処理 */
    async execute() {
      try {
        this.isLoading = true;
        const systemId = individualDiagnose.getCurrentSystemId(this);
        const token = session.getToken();

        // NonDefaultSession遷移、データ読み出し(要求)（バックエンドAPI_POST）
        const response = await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          systemId,
          this.workSupportId,
          {
            id_token: token.id_token,
            type: 'read'
          },
          // エラー発生のコールバック
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
          (result) => { this.validateErrorResult(result); },
        );

        if (response) {
          // ポーリングが正常に完了した場合
          switch (response.data.status) {
            case config.STATUS_WSP_COMPLETED: {
              // 正常終了：読み出しデータ表示
              const systemId = individualDiagnose.getCurrentSystemId(this);
              const showDat = await individualWorkSupportUtil.getReadOutData(this, systemId, response.data.user_data, true);
              this.displayInfo = showDat;
              this.sessionTransitionFlag = response.data.user_data.session_transition_flag;
              this.phase = PHASE.SUCCESS;
              break;
            }
            case config.STATUS_WSP_FAILED:
            default:
              // セッション遷移で通信が失敗していない場合、終了処理を実行するためフラグを更新
              this.sessionTransitionFlag = response.data?.user_data?.session_transition_flag ? response.data.user_data.session_transition_flag : false;
              this.showUnexpectedError();
              break;
          }
        }
      } finally {
        this.isLoading = false;
      }
    },

    /** 
     * OKボタン押下時の処理分岐 
     * @param hasError エラー遷移があるか判断するフラグ
     */
    exitProcess(hasError) {
      if (this.sessionTransitionFlag) {
        // 終了処理
        this.exit(hasError);
      } else {
        this.dispose();
      }
    },

    /**
     * 終了処理
     * @param hasError エラー遷移があるか判断するフラグ
     */
    async exit(hasError) {
      try {
        this.isLoading = true;
        const systemId = individualDiagnose.getCurrentSystemId(this);
        const token = session.getToken();

        // 終了処理(要求)（バックエンドAPI_POST）
        const response = await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          systemId,
          this.workSupportId,
          {
            id_token: token.id_token,
            type: 'exit'
          },
          // エラー発生のコールバック
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
          (result) => { this.validateErrorResult(result); },
        );

        if (response) {
          if (!hasError) {
            this.closeWizard();
            return;
          }
          // ポーリングが正常に完了した場合
          switch (response.data.status) {
            case config.STATUS_WSP_COMPLETED:
              // 正常終了：機能終了
              this.closeWizard();
              break;
            case config.STATUS_WSP_FAILED:
            default:
              // セッション遷移をfalseに設定
              this.sessionTransitionFlag = false;
              // 異常終了：共通エラーに遷移
              this.showUnexpectedError();
              break;
          }
        }
      } finally {
        this.isLoading = false;
      }
    },

    /** Dispose処理 */
    async dispose() {
      try {
        this.isLoading = true;

        const systemId = individualDiagnose.getCurrentSystemId(this);
        const token = session.getToken();

        //バックエンドAPI POST,GET
        await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          systemId,
          this.workSupportId,
          {
            id_token: token.id_token,
            type: 'dispose',
          },
          // エラー発生のコールバックを実装
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
        );

      } finally {
        this.isLoading = false;
        // 結果に関わらず、画面を閉じる
        this.closeWizard();
      }
    },

    /** その他エラー発生時の表示 */
    showUnexpectedError() {
      this.phase = PHASE.FAILED;
    },
  },

  async mounted() {
    this.init();
    // データ読み出し
    this.execute();
  },
};